import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from "@mui/material";
import { useTranslation } from "react-i18next";

const MAX_SHAPE_NAME_LENGTH = 180;

interface EditShapeDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void | Promise<void>;
  editName: string;
  setEditName: (value: string) => void;
  category?: string;
  type: string;
  setType?: (value: string) => void;
  spatial?: "GCJ02" | "WGS84";
  setSpatial?: (value: "GCJ02" | "WGS84") => void;
  saveLoading?: boolean;
  saveDisabled?: boolean;
}

const EditShapeDialog: React.FC<EditShapeDialogProps> = ({
  open,
  onClose,
  onSave,
  editName,
  setEditName,
  category,
  type,
  setType,
  spatial,
  setSpatial,
  saveLoading = false,
  saveDisabled = false
}) => {
  const [t] = useTranslation();
  const shapeTypeValue = category === "polyline" ? "Line" : "Polygon";
  const isShapeNameTooLong = editName.length > MAX_SHAPE_NAME_LENGTH;
  const isSaveDisabled =
    saveLoading || saveDisabled || isShapeNameTooLong || !editName.trim();

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          fontWeight: 500,
          fontSize: "16px",
          lineHeight: "19px",
          letterSpacing: "0.2px",
          color: "#000000de"
        }}
      >
        {t("myShapes.edit-shape")}
      </DialogTitle>

      <DialogContent sx={{ margin: "4px 0" }}>
        {/* 名称 */}
        <TextField
          fullWidth
          variant="outlined"
          label={t("myShapes.shape-name")}
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          size="small"
          autoFocus
          margin="normal"
          color="success"
          error={isShapeNameTooLong}
          sx={{
            "& .MuiInputLabel-root": {
              fontSize: "12px",
              lineHeight: "18px"
            },
            "& .MuiInputLabel-root.Mui-focused": {
              fontSize: "12px",
              lineHeight: "24px"
            },
            "& .MuiOutlinedInput-input": {
              fontSize: "12px"
            }
          }}
        />

        {/* 形状类型下拉框 */}
        {(category === "polyline" || category === "polygon") && (
          <FormControl
            fullWidth
            size="small"
            color="success"
            margin="normal"
            sx={{
              "& .MuiInputLabel-root": {
                fontSize: "12px",
                lineHeight: "24px"
              }
            }}
          >
            <InputLabel>{t("myShapes.type")}</InputLabel>
            <Select
              value={shapeTypeValue}
              onChange={() => undefined}
              disabled
              size="small"
              color="success"
              label={t("myShapes.type")}
              MenuProps={{
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: "12px"
                    }
                  }
                }
              }}
              sx={{
                fontSize: "12px"
              }}
            >
              <MenuItem value="Line">{t("myShapes.type-item.Line")}</MenuItem>
              <MenuItem value="Polygon">
                {t("myShapes.type-item.Region")}
              </MenuItem>
            </Select>
          </FormControl>
        )}

        {/* 项目种类：仅线类型展示 */}
        {category === "polyline" && setType && (
          <FormControl
            fullWidth
            size="small"
            color="success"
            margin="normal"
            sx={{
              "& .MuiInputLabel-root": {
                fontSize: "12px",
                lineHeight: "24px"
              }
            }}
          >
            <InputLabel>{t("myShapes.projectType")}</InputLabel>
            <Select
              value={type}
              onChange={(e) => setType(e.target.value)}
              size="small"
              color="success"
              label={t("myShapes.projectType")}
              MenuProps={{
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: "12px"
                    }
                  }
                }
              }}
              sx={{
                fontSize: "12px"
              }}
            >
              <MenuItem value="Ground">
                {t("myShapes.type-item.Ground")}
              </MenuItem>
              <MenuItem value="Aboveground">
                {t("myShapes.type-item.Aboveground")}
              </MenuItem>
              <MenuItem value="Underground">
                {t("myShapes.type-item.Underground")}
              </MenuItem>
            </Select>
          </FormControl>
        )}

        {setSpatial && (
          <FormControl
            fullWidth
            size="small"
            color="success"
            margin="normal"
            sx={{
              "& .MuiInputLabel-root": {
                fontSize: "12px",
                lineHeight: "24px"
              }
            }}
          >
            <InputLabel>{t("myShapes.coordinate-system")}</InputLabel>
            <Select
              label={t("myShapes.coordinate-system")}
              value={spatial ?? ""}
              onChange={(e) => setSpatial(e.target.value as "GCJ02" | "WGS84")}
              size="small"
              color="success"
              MenuProps={{
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      fontSize: "12px"
                    }
                  }
                }
              }}
              sx={{
                fontSize: "12px"
              }}
            >
              <MenuItem value="GCJ02">
                {t("myShapes.coordinate-system-item.GCJ-02")}
              </MenuItem>
              <MenuItem value="WGS84">
                {t("myShapes.coordinate-system-item.WGS-84")}
              </MenuItem>
            </Select>
          </FormControl>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="success">
          {t("myShapes.cancel")}
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          color="success"
          disabled={isSaveDisabled}
        >
          {saveLoading ? t("myShapes.saving") : t("myShapes.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditShapeDialog;
